'use client';

import React from 'react';
import { parseISO, format } from 'date-fns';
import { useTranslations } from '@fshr/context/i18n-context';
import { formatPrice, pluraliseWord } from '@fshr/shared';
import { Oj } from '@fshr/shared/types';
import HorizontalLine from '../../horizontal-line';
import BevSafeText from '../../bev-safe-text';

export default function ShoppingCartItemRateDetails({
	roomRate,
	currencyCode,
	stayResortFees,
	taxAndFees,
}: {
	roomRate: Oj.ProductRoomRate;
	currencyCode: string;
	stayResortFees: Oj.StayResortFees;
	taxAndFees: string;
}) {
	const t = useTranslations();
	return (
		<div className="bg-surface-recessed spacing-card-padding-xs flex flex-col spacing-stack-xs">
			<div className="rate-details__top">
				<div className="flex flex-row justify-between items-center">
					<span className="text-subtitle2">{t('Average Nightly Rate')}</span>
					<span className="text-subtitle2 text-right">
						{String(
							formatPrice({ value: roomRate?.averageNightlyPrice?.cash?.amount || 0, currencyCode })
						)}
					</span>
				</div>
				<div className="text-caption">{t(String(taxAndFees))}</div>
			</div>
			<HorizontalLine additionalClasses="border-border-subtle" />
			<div className="text-subtitle2">{`${t('Daily Rates')} | ${pluraliseWord(
				roomRate?.nightlyRates?.length || 0,
				t('Night'),
				t('Nights')
			)}`}</div>
			<div className="flex flex-col spacing-stack-2xs">
				{roomRate?.nightlyRates?.map(({ date, cash, resortFees }) => {
					if (date && cash) {
						return (
							<React.Fragment key={date}>
								<div className="flex flex-row justify-between items-center">
									<span className="text-subtitle2">{format(parseISO(date), 'EEEE, MMM dd')}</span>
									<span className="text-subtitle2 text-right">
										{String(formatPrice({ value: cash.amount, currencyCode }))}
									</span>
								</div>
								{resortFees &&
									resortFees.length > 0 &&
									resortFees?.map(({ code, description, cash }) => {
										return (
											<div className="flex flex-row justify-between items-center" key={code}>
												<span className="text-subtitle2">
													<BevSafeText>{`+ ${description}`}</BevSafeText>
												</span>
												<span className="text-subtitle2 text-right">
													{String(formatPrice({ value: cash?.amount || 0, currencyCode }))}
												</span>
											</div>
										);
									})}
							</React.Fragment>
						);
					}
					return null;
				})}
				{stayResortFees &&
					stayResortFees.map(({ code, description, cash }) => {
						return (
							<div className="flex flex-row justify-between items-center" key={code}>
								<span className="text-subtitle2">
									<BevSafeText>{description}</BevSafeText>
								</span>
								<span className="text-subtitle2 text-right">
									{String(formatPrice({ value: cash?.amount || 0, currencyCode }))}
								</span>
							</div>
						);
					})}
			</div>
		</div>
	);
}
